import type { TableDataRow } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { readThrough } from '@/lib/offlineCache';
import { formatValue } from '@/lib/format';
import { friendlyError, isNotFoundError, isPermissionDeniedError, logSwallowed } from '@/lib/errors';

// ============================================================================
// История вызовов скорой (только чтение, клиентский слой).
//
// Источник — обычный документ `ambulance_calls` (создан ядром вертикали
// «Скорая»): отбор по полю-ссылке на СЕРВЕРЕ (пациент — `patient_id`,
// адрес — `address_id`). Представления ссылок и статуса
// (`address_id_presentation`, `status_presentation`) резолвит СЕРВЕР.
//
// Бригада — ТЧ «Бригада» документа; физическая таблица ТЧ строится ядром из
// ИМЕНИ ТЧ (`{документ}_{имя в нижнем регистре}`), поэтому имя и колонки
// (сотрудник/ответственный) берутся из МЕТАДАННЫХ, а не хардкодятся.
// ============================================================================

export const AMBULANCE_CALLS_TABLE = 'ambulance_calls';
export const AMBULANCE_CREW_TS = 'Бригада';
export const AMBULANCE_CREW_TS_CODE = 'ambulance_call_crew';

const PAGE_SIZE = 50;
const CREW_COUNT_LIMIT = 50;

export type AmbulanceState = 'ready' | 'unavailable' | 'denied' | 'error';

export interface AmbulanceCallItem {
    id: string;
    number: string;
    /** Время вызова для показа (dd.MM.yyyy HH:mm). */
    calledAt: string;
    /** Исходное значение времени — для сортировки. */
    sortAt: string;
    address: string;
    patient: string;
    statusCode: string;
    statusLabel: string;
    outcome: string;
    /** Станция, принявшая вызов (представление сервера). */
    station: string;
    /** «Бригада: N чел.»; null — счётчик недоступен (ТЧ не прочиталась). */
    crewCount: number | null;
    /** ФИО ответственного из бригады; пусто — не определён. */
    crewResponsible: string;
}

export interface AmbulanceHistory {
    state: AmbulanceState;
    items: AmbulanceCallItem[];
    /** Всего вызовов по отбору на сервере (может быть больше показанных). */
    total?: number;
    error: string;
    note: string;
}

/** Сводка по бригаде вызова: количество строк + ответственный. */
export interface CrewMemberInfo {
    count: number | null;
    responsible: string;
}

type Row = Record<string, string> & { id: string };

function slug(): string {
    return useCenterStore.getState().baseSlug || 'local';
}

function cellsOf(row: TableDataRow): Record<string, string> {
    const out: Record<string, string> = {};
    for (const value of row.values ?? []) {
        out[String(value.column)] = value.value === null || value.value === undefined
            ? ''
            : String(value.value);
    }
    return out;
}

function rowsOf(rows: TableDataRow[]): Row[] {
    return rows.map((row) => ({ id: String(row.id || ''), ...cellsOf(row) }));
}

function dateTimeText(value: string): string {
    return formatValue(value, 'date:dd.MM.yyyy HH:mm', 'timestamp') || value;
}

function isTruthy(value: unknown): boolean {
    const v = String(value ?? '').trim().toLowerCase();
    return v === 'true' || v === '1' || v === 'да' || v === 'yes';
}

/** Сортировка вызовов: новые сверху; без времени — в конец. */
export function sortCallsDesc(items: AmbulanceCallItem[]): AmbulanceCallItem[] {
    return [...items].sort((a, b) => {
        if (!a.sortAt && !b.sortAt) return 0;
        if (!a.sortAt) return 1;
        if (!b.sortAt) return -1;
        return b.sortAt.localeCompare(a.sortAt);
    });
}

/** Преобразование строк документа в элементы истории (SSoT полей — отчёт ядра). */
export function mapCallRows(
    rows: Row[],
    crewCounts: Record<string, number | null> = {},
    crewResponsible: Record<string, string> = {},
): AmbulanceCallItem[] {
    const items = rows.map((row) => {
        const calledAt = row.called_at || row.date || '';
        return {
            id: row.id,
            number: row.number || '',
            calledAt: dateTimeText(calledAt),
            sortAt: calledAt,
            address: row.address_id_presentation || row.address_raw || '',
            patient: row.patient_id_presentation || row.patient_name_raw || '',
            statusCode: row.status || '',
            statusLabel: row.status_presentation || row.status || '',
            outcome: row.outcome_code_presentation || row.outcome || '',
            station: row.station_id_presentation || '',
            crewCount: Object.prototype.hasOwnProperty.call(crewCounts, row.id)
                ? crewCounts[row.id]
                : null,
            crewResponsible: crewResponsible[row.id] || '',
        };
    }).filter((item) => item.id !== '');
    return sortCallsDesc(items);
}

/** Сводка по строкам ТЧ бригады (колонки — из метаданных ТЧ, не хардкод). */
export function summarizeCrew(rows: Row[], employeeColumn: string, responsibleColumn: string): CrewMemberInfo {
    const count = rows.length;
    if (!employeeColumn || rows.length === 0) return { count, responsible: '' };
    const responsibleRow = responsibleColumn ? rows.find((row) => isTruthy(row[responsibleColumn])) : undefined;
    const row = responsibleRow || rows[0];
    // Только СЕРВЕРНОЕ представление — UUID сотрудника наружу не показываем.
    const name = String(row[`${employeeColumn}_presentation`] || '');
    return { count, responsible: name };
}

/** Физическая таблица ТЧ «Бригада» (по имени из метаданных). */
export function crewTableName(section: { name?: string; synonym?: string; code?: string }): string {
    const suffix = String(section.name || section.synonym || section.code || '').trim();
    if (!suffix) return '';
    return `${AMBULANCE_CALLS_TABLE}_${suffix.toLowerCase()}`;
}

interface CrewMeta {
    table: string;
    employeeColumn: string;
    responsibleColumn: string;
}

let crewMetaCache: { slug: string; promise: Promise<CrewMeta | null> } | null = null;
let crewWarningLogged = false;

async function fetchCrewMeta(): Promise<CrewMeta | null> {
    const response = await getSDK().metadata.listTabularSections({
        entityType: 'document',
        entityCode: AMBULANCE_CALLS_TABLE,
    });
    const section = (response.items || []).find((item) => {
        const values = [item.name, item.synonym, item.code].map((v) => String(v || '').toLowerCase());
        return values.includes(AMBULANCE_CREW_TS.toLowerCase())
            || values.includes(AMBULANCE_CREW_TS_CODE.toLowerCase());
    });
    if (!section) return null;
    const table = crewTableName(section);
    if (!table) return null;
    const columns = section.columns || [];
    const label = (column: { name?: string; synonym?: string }) => `${column.name || ''} ${column.synonym || ''}`;
    const employeeColumn = columns.find((column) => {
        const ref = String(column.refCatalogCode || '').toLowerCase();
        return ref === 'employees' || /сотрудник/i.test(label(column));
    });
    const responsibleColumn = columns.find((column) => {
        const dataType = String(column.dataType || '').toLowerCase();
        return dataType === 'boolean' && /ответ/i.test(label(column));
    });
    return {
        table,
        employeeColumn: String(employeeColumn?.name || ''),
        responsibleColumn: String(responsibleColumn?.name || ''),
    };
}

async function resolveCrewMeta(): Promise<CrewMeta | null> {
    const currentSlug = slug();
    if (!crewMetaCache || crewMetaCache.slug !== currentSlug) {
        const promise: Promise<CrewMeta | null> = fetchCrewMeta().catch((e) => {
            if (crewMetaCache?.promise === promise) crewMetaCache = null;
            if (!crewWarningLogged) {
                crewWarningLogged = true;
                logSwallowed(e, 'История вызовов: табличная часть «Бригада»');
            }
            return null;
        });
        crewMetaCache = { slug: currentSlug, promise };
    }
    return crewMetaCache.promise;
}

/** Бригада по каждому вызову: число строк и ответственный (ошибки — null). */
async function loadCrewInfo(callIds: string[]): Promise<Record<string, CrewMemberInfo>> {
    const result: Record<string, CrewMemberInfo> = {};
    if (callIds.length === 0) return result;
    const meta = await resolveCrewMeta();
    if (!meta) return result;
    const limit = callIds.slice(0, CREW_COUNT_LIMIT);
    await Promise.all(limit.map(async (id) => {
        try {
            const response = await getSDK().metadata.listTableData({
                schema: 'public',
                table: meta.table,
                pageSize: 200,
                page: 0,
                filters: [{ column: 'parent_id', value: id }],
            });
            result[id] = summarizeCrew(rowsOf(response.rows || []), meta.employeeColumn, meta.responsibleColumn);
        } catch (e) {
            result[id] = { count: null, responsible: '' };
            if (!crewWarningLogged) {
                crewWarningLogged = true;
                logSwallowed(e, 'История вызовов: строки бригады');
            }
        }
    }));
    return result;
}

async function fetchCalls(filterColumn: string, filterValue: string, orderBy?: string): Promise<{ items: AmbulanceCallItem[]; total: number }> {
    const response = await getSDK().metadata.listTableData({
        schema: 'public',
        table: AMBULANCE_CALLS_TABLE,
        pageSize: PAGE_SIZE,
        page: 0,
        filters: [{ column: filterColumn, value: filterValue }],
        orderBy: orderBy || '',
        orderDesc: !!orderBy,
    });
    const rows = rowsOf(response.rows).filter((row) => row.id !== '');
    const crew = await loadCrewInfo(rows.map((row) => row.id));
    const counts: Record<string, number | null> = {};
    const responsible: Record<string, string> = {};
    for (const [id, info] of Object.entries(crew)) {
        counts[id] = info.count;
        if (info.responsible) responsible[id] = info.responsible;
    }
    return { items: mapCallRows(rows, counts, responsible), total: Number(response.totalCount || rows.length) };
}

function unavailable(note: string): AmbulanceHistory {
    return { state: 'unavailable', items: [], error: '', note };
}

function toHistoryError(e: unknown, unavailableNote: string): AmbulanceHistory {
    if (isPermissionDeniedError(e)) {
        return { state: 'denied', items: [], error: 'Нет прав на чтение вызовов скорой', note: '' };
    }
    if (isNotFoundError(e)) {
        return unavailable(unavailableNote);
    }
    logSwallowed(e, 'История вызовов скорой');
    return { state: 'error', items: [], error: friendlyError(e), note: '' };
}

/** История вызовов пациента (readThrough — офлайн отдаёт сохранённое). */
export async function loadAmbulanceHistory(patientId: string): Promise<AmbulanceHistory> {
    if (!patientId) return unavailable('Не указан пациент.');
    try {
        const result = await readThrough(
            `patient-card:ambulance:v2:${slug()}:${patientId}`,
            () => fetchCalls('patient_id', patientId),
        );
        return { state: 'ready', items: result.data.items, total: result.data.total, error: '', note: '' };
    } catch (e) {
        return toHistoryError(e, 'Раздел «Скорая» в этой базе не ведётся.');
    }
}

/**
 * История вызовов по адресу (пункт раздела «История по адресу»).
 * Отбор — точный `address_id` (уровень дома). Уровни «улица/город» требуют
 * отбора IN по адресам и вынесены в отчёт как следующий шаг.
 */
export async function loadAmbulanceAddressHistory(addressId: string): Promise<AmbulanceHistory> {
    if (!addressId) return unavailable('Не выбран адрес.');
    try {
        const result = await readThrough(
            `ambulance-address:v1:${slug()}:${addressId}`,
            () => fetchCalls('address_id', addressId, 'called_at'),
        );
        return { state: 'ready', items: result.data.items, total: result.data.total, error: '', note: '' };
    } catch (e) {
        return toHistoryError(e, 'Раздел «Скорая» в этой базе не ведётся.');
    }
}
